import { Row, Col, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../../hooks/api";
import Loading from "../../../../components/core/blocks/Loading";
import { formatCurrency, formatNumber } from "../../../../utils/formatNumber";
import moment from "moment";
import { keepPreviousData } from "@tanstack/react-query";

const DiffRow = ({ history, createdOn, user, field }) => {
  const rows = [];

  const keys = Object.keys(history);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = history[key];

    if (value?.previous === null || value?.new === null) {
      continue;
    }

    rows.push(
      <tr key={i}>
        <td>{field[key].name}</td>
        <td>{moment(createdOn).format("YYYY-MM-DD HH:mm A")}</td>
        <td className="text-end">{field[key].formatter(value.previous)}</td>
        <td className="text-end">{field[key].formatter(value.new)}</td>
        <td className="text-end">{`${user.first_name} ${user.last_name} (${user.email})`}</td>
      </tr>,
    );
  }

  return rows;
};

export const PricingPlanHistoryTab = ({ pricingPlan, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const filters = {
    pricing_plan_id: pricingPlan?.id,
  };
  const { data: historyData, isLoading: historyLoading } = useFetch(
    ["pricingplan_history", marketPlace.marketPlace, pricingPlan?.id],
    `/pricing/pricingplan/history`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: pricingPlan !== null && pricingPlan !== undefined,
    },
  );
  if (!pricingPlan) {
    return null;
  }

  if (historyLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  const CurrencyFormatter = (value) => {
    return formatCurrency(value, {
      marketPlace: marketPlace.marketPlace,
    });
  };

  const field = {
    plan_name: {
      name: t(($) => $.pricingv2["Plan Name"]),
      formatter: (value) => value,
    },
    max_price: {
      name: t(($) => $.pricingv2["Max Price"]),
      formatter: CurrencyFormatter,
    },
    min_price: {
      name: t(($) => $.pricingv2["Min Price"]),
      formatter: CurrencyFormatter,
    },
    target_bsr: {
      name: t(($) => $.pricingv2["Target BSR"]),
      formatter: (value) => formatNumber(value, { decimalPlaces: 0 }),
    },
    configured_slider_value: {
      name: t(($) => $.pricingv2["Strategy"]),
      formatter: (value) => {
        if (value === -2) {
          return t(($) => $.pricingv2["Maximize Volume"]);
        } else if (value === -1) {
          return t(($) => $.pricingv2["Boost Volume"]);
        } else if (value === 0) {
          return t(($) => $.pricingv2.strategy["Balanced"]);
        } else if (value === 2) {
          return t(($) => $.pricingv2["Boost Margin"]);
        }
        return t(($) => $.pricingv2["Maximize Margin"]);
      },
    },
    status: {
      name: t(($) => $.pricingv2["Plan Status"]),
      formatter: (value) => {
        if (value === "enabled") {
          return t(($) => $.pricingv2["Active"]);
        } else if (value === "disabled") {
          return t(($) => $.pricingv2["Disabled"]);
        }
        return t(($) => $.pricingv2["Unknown"]);
      },
    },
    relationship_rules: {
      name: t(($) => $.pricingv2["Relationship rules"]),
      formatter: (value) => {
        const entries = Object.entries(value);
        if (entries.length === 0) {
          return t(($) => $.pricingv2["None"]);
        }
        return entries.map(([key, rule]) => `${key}: ${rule.action} by ${rule.value} ${rule.unit}`);
      },
    },
    exclude_sns: {
      name: t(($) => $.pricingv2["Exclude Subscribe & Save"]),
      formatter: (value) => (value ? t(($) => $.pricingv2["Yes"]) : t(($) => $.pricingv2["No"])),
    },
  };

  return (
    <Row className="p-3">
      <Col>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th className="text-start">{t(($) => $.pricingv2["Field"])}</th>
              <th className="text-start">{t(($) => $.pricingv2["Date"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Previous Value"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["New Value"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["User"])}</th>
            </tr>
          </thead>

          <tbody>
            {historyData?.results?.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center p-5">
                  {t(($) => $.pricingv2["The Pricing Plan has not been recently updated."])}
                </td>
              </tr>
            )}
            {historyData?.results?.map((history, index) => (
              <DiffRow
                history={history?.diff}
                key={index}
                createdOn={history?.created_on}
                user={history.updated_by}
                field={field}
              />
            ))}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};
